import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { useLpcPropertyEvidence } from '~/hooks/apis/reporting/useLpcOfferHealth';

import CommercialHistory from './CommercialHistory';
import DataCoverage from './DataCoverage';
import HotelPerformance from './HotelPerformance';
import WorkspaceNotes from './WorkspaceNotes';
import { lppMappingStatus } from './lppMargin';
import { money, number, percent } from './review';
import { unresolvedSourceWarnings } from './sourceIssues';
import type { HealthProperty, HealthSnapshot, PerformanceDateRange, ReviewWindow } from './types';

export default function HotelEvidence({
  property,
  snapshot,
  window,
  dateRange,
}: {
  property: HealthProperty;
  snapshot: HealthSnapshot;
  window: ReviewWindow;
  dateRange?: PerformanceDateRange;
}) {
  const query = useLpcPropertyEvidence(property.property_id, snapshot.snapshot_id, dateRange);
  if (query.isPending) return <Skeleton variant="rounded" height={300} aria-label="Loading hotel evidence" />;
  if (query.isError)
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" onClick={() => void query.refetch()}>
            Retry
          </Button>
        }
      >
        Hotel evidence could not be loaded. Retry to load this hotel for the selected dates.
      </Alert>
    );
  const { commercial, impressions, parity } = query.data.evidence;
  const lppMapping = lppMappingStatus(query.data.evidence.scope.live_lpp_offer_bk);
  const history = query.data.evidence.commercial_history ?? [];
  const checks = parity[0];
  // Historical diagnostic observations may include other channels; only these are actionable benchmarks.
  const examples = (checks?.recheck_examples ?? []).filter((example) =>
    ['Official Site', 'Booking.com', 'Expedia', 'Trip.com'].includes(example.competitor),
  );
  return (
    <Stack gap={2}>
      <Box sx={{ py: 1, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={1}>
          <Typography variant="h5" sx={{ color: 'primary.main' }}>
            {property.property_name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, pt: { md: 0.5 } }}>
            Snapshot · {snapshot.report_date}
          </Typography>
        </Stack>
        <Box
          sx={{
            mt: 2,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(3, max-content)' },
            columnGap: 5,
            rowGap: 1.5,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Country
            </Typography>
            <Typography variant="body2">{property.country || 'Unavailable'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Commercial contact
            </Typography>
            <Typography variant="body2">{property.market_manager || 'Unavailable'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              LPP connection
            </Typography>
            <Tooltip title="Property mapping in this snapshot, independent of sales in the selected period.">
              <Typography
                variant="body2"
                sx={{ color: lppMapping === 'mapped' ? 'primary.main' : 'text.secondary', fontWeight: 600 }}
              >
                {lppMapping === 'mapped' ? 'Mapped' : lppMapping === 'unmapped' ? 'Not mapped' : 'Unavailable'}
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      </Box>
      {unresolvedSourceWarnings(query.data.metadata.source_warnings ?? snapshot.source_warnings, [
        property.property_id,
      ]).map((warning) => (
        <Alert key={warning.id} severity={warning.severity}>
          {warning.title}: {warning.detail}
        </Alert>
      ))}
      <HotelPerformance
        property={property}
        window={window}
        lppMapping={lppMapping}
        comparisonEndDate={dateRange?.end_date ?? snapshot.report_date}
      />
      <Typography variant="body2" color="text.secondary">
        Snapshot observations · {snapshot.report_date} · Price checks and package impressions are available for this day
        only.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.3fr 1fr' }, gap: 2 }}>
        <Card variant="outlined">
          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1}>
              <Typography variant="subtitle1" fontWeight={600}>
                Rate competitiveness
              </Typography>
              <Tooltip title="Win or neutral share across Booking.com, Expedia, Trip.com, Agoda and official hotel sites. Neutral means within 2% of the LPC nightly price.">
                <Typography
                  variant="caption"
                  color="text.secondary"
                  tabIndex={0}
                  sx={{ textDecoration: 'underline dotted', cursor: 'help' }}
                >
                  How it is calculated
                </Typography>
              </Tooltip>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Price checks on {snapshot.report_date}
            </Typography>
            <Stack direction="row" alignItems="baseline" gap={1.5} sx={{ mt: 1 }}>
              <Typography variant="h3" sx={{ color: 'primary.main' }}>
                {checks?.parity_score == null ? '—' : percent(checks.parity_score)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                win or match rate
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {checks
                ? `${number(checks.wins + checks.price_neutral)} of ${number(checks.tracked_competitor_checks)} classified checks were competitive`
                : 'No classified price checks for this date'}
            </Typography>
            {checks && checks.tracked_competitor_checks > 0 && (
              <>
                <Box
                  aria-label={`${number(checks.wins)} wins, ${number(checks.price_neutral)} neutral, ${number(checks.losses)} losses`}
                  sx={{ display: 'flex', height: 10, borderRadius: 1, overflow: 'hidden', bgcolor: 'action.hover' }}
                >
                  <Box
                    sx={{
                      width: `${(checks.wins / checks.tracked_competitor_checks) * 100}%`,
                      bgcolor: 'primary.main',
                    }}
                  />
                  <Box
                    sx={{
                      width: `${(checks.price_neutral / checks.tracked_competitor_checks) * 100}%`,
                      bgcolor: 'text.secondary',
                    }}
                  />
                  <Box
                    sx={{ width: `${(checks.losses / checks.tracked_competitor_checks) * 100}%`, bgcolor: 'divider' }}
                  />
                </Box>
                <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mt: 1 }}>
                  <Typography variant="caption">
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {number(checks.wins)}
                    </Box>{' '}
                    wins
                  </Typography>
                  <Typography variant="caption">
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {number(checks.price_neutral)}
                    </Box>{' '}
                    neutral
                  </Typography>
                  <Typography variant="caption">
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {number(checks.losses)}
                    </Box>{' '}
                    losses
                  </Typography>
                </Stack>
              </>
            )}
          </CardContent>
        </Card>
        <Card variant="outlined">
          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Package visibility
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Impressions on {snapshot.report_date}
            </Typography>
            <Stack direction="row" gap={4} sx={{ mt: 1.5 }}>
              {['LPC', 'LPP'].map((channel) => {
                const row = impressions.find((item) => item.channel === channel);
                return (
                  <Box key={channel}>
                    <Typography variant="body2" color="text.secondary">
                      {channel}
                    </Typography>
                    <Typography variant="h4" sx={{ mt: 0.5 }}>
                      {number(row?.impression_observed_rows ? row.impressions : null)}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
              Package-option exposures; a person can see more than one option.
            </Typography>
          </CardContent>
        </Card>
      </Box>
      {examples.length > 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Price checks to revisit
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Dated observations to investigate. Recheck equivalent rooms, board and cancellation terms before changing
              a rate.
            </Typography>
            <TableContainer>
              <Table size="small" aria-label="Historical parity checks to revisit" sx={{ minWidth: 820 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Stay dates</TableCell>
                    <TableCell>Room / board</TableCell>
                    <TableCell>Benchmark</TableCell>
                    <TableCell align="right">LPC nightly</TableCell>
                    <TableCell align="right">Benchmark nightly</TableCell>
                    <TableCell align="right">
                      <Tooltip title="LPC nightly price premium relative to the benchmark price. This uses the saved observation; recheck equivalent terms before changing a rate.">
                        <Box component="span">LPC premium</Box>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {examples.map((example, index) => (
                    <TableRow key={`${example.rate_id}-${example.check_in_date}-${index}`}>
                      <TableCell sx={{ minWidth: 170 }}>
                        {example.check_in_date} → {example.check_out_date}
                      </TableCell>
                      <TableCell>
                        {example.room_name || 'Room unavailable'}
                        <Typography variant="caption" display="block">
                          {example.board_code || 'Board unavailable'} ·{' '}
                          <Tooltip title={`Rate ID: ${example.rate_id}`}>
                            <Box component="span" tabIndex={0} sx={{ textDecoration: 'underline dotted' }}>
                              Rate details
                            </Box>
                          </Tooltip>
                        </Typography>
                      </TableCell>
                      <TableCell>{example.competitor_name || example.competitor}</TableCell>
                      <TableCell align="right">
                        {example.le_price_nightly.toLocaleString('en-AU', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{' '}
                        {example.currency}
                      </TableCell>
                      <TableCell align="right">
                        {example.competitor_price_nightly.toLocaleString('en-AU', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{' '}
                        {example.currency}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {example.gap_pct > 0 ? '+' : ''}
                        {percent(example.gap_pct)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ px: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            No individual price-check examples are saved for this date. Recheck equivalent room, board and cancellation
            terms before changing a rate.
          </Typography>
        </Box>
      )}
      <CommercialHistory rows={history} date={snapshot.report_date} range={dateRange} />
      <Accordion
        disableGutters
        elevation={0}
        sx={{ border: 1, borderColor: 'divider', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2" fontWeight={600}>
            Data and definitions
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Performance totals and the margin history use{' '}
            {snapshot.attribution_model || 'the snapshot attribution model'}. The latest-day figures below include
            transactions marked SPP-active (single property page), a different source basis.
          </Typography>
          <Box>
            <Typography variant="h6" gutterBottom>
              Latest-day sales and refunds
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              After refunds, AUD · {snapshot.report_date} · SPP-active transactions
            </Typography>
            <TableContainer>
              <Table size="small" aria-label="Daily hotel commercial observations">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">TTV</TableCell>
                    <TableCell align="right">Margin</TableCell>
                    <TableCell>Margin records</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {commercial.map((row) => (
                    <TableRow key={row.product_bk}>
                      <TableCell>{row.product_bk === 'tactical_ao_hotel' ? 'LPC' : 'LPP'}</TableCell>
                      <TableCell align="right">{money(row.ttv_observed_rows ? row.ttv_ar_aud : null)}</TableCell>
                      <TableCell align="right">{money(row.margin_observed_rows ? row.margin_ar_aud : null)}</TableCell>
                      <TableCell>
                        {row.source_rows
                          ? `${number(row.margin_observed_rows)} of ${number(row.source_rows)} records`
                          : 'No records for this date'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Divider sx={{ my: 3 }} />
          <DataCoverage property={property} date={snapshot.report_date} />
          <Divider sx={{ my: 3 }} />
          <WorkspaceNotes />
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}
